// 行事曆 API 的前端呼叫（GDO-24～GDO-27）。寫入類請求帶 x-dayone 標頭（伺服器用來擋跨站請求）。
import type { CalendarEvent, UnboundEvent } from './status.ts';

export type CalendarErrorCode =
  | 'not_configured'
  | 'signed_out'
  | 'reauth_required'
  | 'forbidden'
  | 'not_found'
  | 'calendar_not_found'
  | 'not_day'
  | 'period_passed'
  | 'stale_snapshot'
  | 'bound_event'
  | 'google_unavailable'
  | 'network'
  | 'unknown';

export class CalendarApiError extends Error {
  readonly code: CalendarErrorCode;
  constructor(code: CalendarErrorCode, message: string = code) {
    super(message);
    this.code = code;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(method !== 'GET' ? { 'x-dayone': '1' } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new CalendarApiError('network');
  }
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as (T & { error?: { code?: string; message?: string } }) | null;
  if (!res.ok) throw new CalendarApiError((json?.error?.code ?? 'unknown') as CalendarErrorCode, json?.error?.message);
  return json as T;
}

export interface Me {
  email: string;
}
export interface CalendarInfo {
  id: string;
  name: string;
  primary: boolean;
  color?: string;
}
export interface StatusResponse {
  calendarId: string;
  events: CalendarEvent[];
  unbound: UnboundEvent[];
}
export interface GameRef {
  gameId: string;
  /** 清單列上的詳情 hash：伺服器依它讀取快照中的詳情 */
  x: string;
  /** 「我的平台」：事件日期以這些平台中最早的日期為準 */
  platforms: string[];
}
export type SavedEvent = CalendarEvent & { unchanged?: boolean };

const q = (calendarId: string) => `calendarId=${encodeURIComponent(calendarId)}`;

export const calendarApi = {
  me: () => request<Me>('GET', '/api/auth/me'),
  logout: () => request<void>('POST', '/api/auth/logout'),
  loginUrl: (returnTo: string) => `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`,
  calendars: () => request<{ calendars: CalendarInfo[] }>('GET', '/api/calendar/calendars'),
  status: (calendarId: string) => request<StatusResponse>('GET', `/api/calendar/status?${q(calendarId)}`),
  add: (calendarId: string, game: GameRef) =>
    request<SavedEvent>('POST', '/api/calendar/events', { calendarId, ...game }),
  update: (calendarId: string, eventId: string, game: GameRef) =>
    request<SavedEvent>('PUT', `/api/calendar/events/${encodeURIComponent(eventId)}`, { calendarId, ...game }),
  remove: (calendarId: string, eventId: string) =>
    request<void>('DELETE', `/api/calendar/events/${encodeURIComponent(eventId)}?${q(calendarId)}`),
  bind: (calendarId: string, eventId: string, game: GameRef) =>
    request<SavedEvent>('POST', '/api/calendar/bind', { calendarId, eventId, ...game }),
  unbind: (calendarId: string, eventId: string) =>
    request<void>('POST', '/api/calendar/unbind', { calendarId, eventId }),
};
